import { useCallback, RefObject, MutableRefObject } from 'react';
import { select, Selection } from 'd3-selection';
import { ZoomBehavior, ZoomTransform, zoomIdentity } from 'd3-zoom';
import { Simulation } from 'd3-force';
import { D3Node } from '../../../types/erd';
import { getNodeDimensions } from '../../../utils/erd.utils';
// import { D3Node } from '../interfaces/erd.interfaces';
// import { getNodeDimensions } from '../utils/erd.utils';

interface UseZoomControlsParams {
  svgRef: RefObject<SVGSVGElement | null>;
  zoomBehaviorRef: MutableRefObject<ZoomBehavior<SVGSVGElement, unknown> | null>;
  initialTransformRef: MutableRefObject<ZoomTransform | null>;
  nodeContainerRef: MutableRefObject<Selection<SVGGElement, unknown, null, undefined> | null>;
  nodesRef: MutableRefObject<D3Node[]>;
  initialNodePositionsRef: MutableRefObject<Map<string, { x: number; y: number }>>;
  simulationRef: MutableRefObject<Simulation<D3Node, undefined> | null>;
  visibleTypesRef: MutableRefObject<Set<string>>;
  dimensions: { width: number; height: number };
  renderEdgesOnly: () => void;
}

export function useZoomControls({
  svgRef,
  zoomBehaviorRef,
  initialTransformRef,
  nodeContainerRef,
  nodesRef,
  initialNodePositionsRef,
  simulationRef,
  visibleTypesRef,
  dimensions,
  renderEdgesOnly,
}: UseZoomControlsParams): {
  handleZoomIn: VoidFunction;
  handleZoomOut: VoidFunction;
  handleResetToInitial: VoidFunction;
  handleResetZoom: VoidFunction;
} {

  const handleZoomIn = useCallback(() => {
    if (svgRef.current && zoomBehaviorRef.current) {
      select(svgRef.current).call(zoomBehaviorRef.current.scaleBy, 1.3);
    }
  }, [svgRef, zoomBehaviorRef]);

  const handleZoomOut = useCallback(() => {
    if (svgRef.current && zoomBehaviorRef.current) {
      select(svgRef.current).call(zoomBehaviorRef.current.scaleBy, 0.7);
    }
  }, [svgRef, zoomBehaviorRef]);

  const handleResetZoom = useCallback(() => {
    if (svgRef.current && zoomBehaviorRef.current && simulationRef.current) {
      const { width, height } = dimensions;
      const nodes = simulationRef.current.nodes();

      const visibleNodes = nodes.filter((n: D3Node) => visibleTypesRef.current.has(n.id));

      if (visibleNodes.length === 0) {
        const initialTransform = zoomIdentity.translate(width / 2, height / 2).scale(0.5);
        select(svgRef.current).call(zoomBehaviorRef.current.transform, initialTransform);
        return;
      }

      const padding = 100;
      let minX = Number.POSITIVE_INFINITY,
        minY = Number.POSITIVE_INFINITY,
        maxX = Number.NEGATIVE_INFINITY,
        maxY = Number.NEGATIVE_INFINITY;

      visibleNodes.forEach((node: D3Node) => {
        const dims = getNodeDimensions(node.label);
        minX = Math.min(minX, (node.x || 0) - dims.width / 2);
        maxX = Math.max(maxX, (node.x || 0) + dims.width / 2);
        minY = Math.min(minY, (node.y || 0) - dims.height / 2);
        maxY = Math.max(maxY, (node.y || 0) + dims.height / 2);
      });

      minX -= padding;
      minY -= padding;
      maxX += padding;
      maxY += padding;

      const graphWidth = maxX - minX;
      const graphHeight = maxY - minY;
      const scale = Math.max(0.1, Math.min(width / graphWidth, height / graphHeight, 2));

      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;

      const transform = zoomIdentity
        .translate(width / 2 - centerX * scale, height / 2 - centerY * scale)
        .scale(scale);

      select(svgRef.current)
        .transition()
        .duration(0) // Instant, no animation
        .call(zoomBehaviorRef.current.transform, transform);
    }
  }, [svgRef, zoomBehaviorRef, simulationRef, visibleTypesRef, dimensions]);

  const handleResetToInitial = useCallback(() => {
    // If initial state is not saved yet, just call handleResetZoom to fit all nodes
    if (!initialTransformRef.current || initialNodePositionsRef.current.size === 0) {
      handleResetZoom();
      return;
    }

    if (svgRef.current && zoomBehaviorRef.current && nodeContainerRef.current) {
      const nodes = nodesRef.current;
      nodes.forEach((node) => {
        const initialPos = initialNodePositionsRef.current.get(node.id);
        if (initialPos) {
          node.x = initialPos.x;
          node.y = initialPos.y;
          node.fx = null;
          node.fy = null;
        }
      });

      nodeContainerRef.current
        .selectAll('.node')
        .attr('transform', (d: any) => `translate(${d.x},${d.y})`);

      renderEdgesOnly();

      select(svgRef.current).call(zoomBehaviorRef.current.transform, initialTransformRef.current);
    }
  }, [svgRef, zoomBehaviorRef, initialTransformRef, nodeContainerRef, nodesRef, initialNodePositionsRef, renderEdgesOnly, handleResetZoom]);

  return {
    handleZoomIn,
    handleZoomOut,
    handleResetToInitial,
    handleResetZoom,
  };
}
